import {
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  HStack,
  Input,
  InputGroup,
  InputRightAddon,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Text,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Select } from 'chakra-react-select';
import { useState } from 'react';
import { Controller, Resolver, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslations } from 'use-intl';

import { noop } from '@blockpulse3/data/shared';
import { UboRole, useMeQuery } from '@blockpulse3/graphql/hooks';
import { alpha3CountryOptions, formatCountry } from '@blockpulse3/helpers';
import { ErrorMessage } from '@blockpulse3/ui/commons';

import { ownerFormDefaultValues, schemaUbo } from './schema';
import { OwnerForm } from './types';

type Props = {
  /* ** Is the switch to prefill user data disabled ** */
  isUserSwitchDisabled: boolean;
  /* ** Default form values ** */
  defaultValues?: OwnerForm;
  /* ** Max sharePercentage value. `100 - (sum of previous owners)` ** */
  maxSharePercentage: number;
  /* ** Callback on form cancel ** */
  onCancel?: () => void;
  /* ** Callback on form submit ** */
  onSubmit?: (data: OwnerForm) => void;
};

export function CompanyOwnerForm({
  isUserSwitchDisabled,
  defaultValues = ownerFormDefaultValues,
  maxSharePercentage,
  onCancel = noop,
  onSubmit = noop,
}: Props): JSX.Element {
  const i18nGeneral = useTranslations('General');
  const i18nOwners = useTranslations('OwnersForm');
  const i18nShareholderStatus = useTranslations('ShareholderStatusValues');

  const [isUserChecked, setIsUserChecked] = useState(false);

  /* ** Use MeQuery for `onCompleted` callback access ** */
  const { data } = useMeQuery({
    onCompleted: (data) => {
      setIsUserChecked(data.me?.individualIdentity?.id === defaultValues.individualIdentityId);
    },
  });

  const { register, control, formState, watch, setValue, reset, handleSubmit } = useForm<OwnerForm>(
    {
      defaultValues,
      resolver: yupResolver(schemaUbo) as Resolver<OwnerForm>,
    },
  );

  const user = data?.me;
  const isRepresentativeStatus = watch('role') === UboRole.LegalRepresentative;

  const handleUserSwitch = (): void => {
    if (!user) {
      return;
    }

    if (!isUserChecked) {
      setValue('individualIdentityId', user.individualIdentity?.id);
      setValue('firstName', user.firstName);
      setValue('lastName', user.lastName);
      setValue('birthdate', user.individualIdentity?.birthdate || '');
      setValue('birthplace', user.individualIdentity?.birthplace || '');
      setValue('address.line', user.individualIdentity?.address?.line || '');
      setValue('address.city', user.individualIdentity?.address?.city || '');
      setValue('address.postalCode', user.individualIdentity?.address?.postalCode || '');
      setValue('address.country', {
        label: formatCountry(user.individualIdentity?.address?.country || 'FRA'),
        value: user.individualIdentity?.address?.country || 'FRA',
      });
      setIsUserChecked(true);
    } else {
      reset({ ...ownerFormDefaultValues });
      setIsUserChecked(false);
    }
  };

  const handleFormCancel = (): void => {
    onCancel();
  };

  const handleFormSubmit: SubmitHandler<OwnerForm> = (data): void => {
    onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <Stack spacing="8">
        <Stack spacing="4">
          <Stack direction={{ base: 'column', md: 'row' }}>
            <FormControl alignItems="center" display="flex" isDisabled={isUserSwitchDisabled}>
              <Switch id="isUser" isChecked={isUserChecked} onChange={handleUserSwitch} />
              <FormLabel htmlFor="isUser" mb="0" ml="2">
                <Text>
                  {i18nGeneral('ItsMe')},
                  <Text as="span" fontWeight="bold">
                    {` ${user?.firstName} ${user?.lastName}`}
                  </Text>
                </Text>
              </FormLabel>
            </FormControl>
          </Stack>
          <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
            <FormControl isInvalid={!!formState.errors?.firstName}>
              <FormLabel htmlFor="firstName">{i18nOwners('FirstNameField')}</FormLabel>
              <Input id="firstName" type="text" {...register('firstName')} />
              <ErrorMessage error={formState.errors?.firstName} />
            </FormControl>
            <FormControl isInvalid={!!formState.errors?.lastName}>
              <FormLabel htmlFor="lastName">{i18nOwners('LastNameField')}</FormLabel>
              <Input id="lastName" type="text" {...register('lastName')} />
              <ErrorMessage error={formState.errors?.lastName} />
            </FormControl>
          </Stack>
          <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
            <FormControl isInvalid={!!formState.errors?.birthdate}>
              <FormLabel htmlFor="birthdate">{i18nOwners('BirthdateField')}</FormLabel>
              <Input id="birthdate" type="date" {...register('birthdate')} />
              <ErrorMessage error={formState.errors?.birthdate} />
            </FormControl>
            <FormControl isInvalid={!!formState.errors?.birthplace}>
              <FormLabel htmlFor="birthplace">{i18nOwners('BirthplaceField')}</FormLabel>
              <Input id="birthplace" type="text" {...register('birthplace')} />
              <ErrorMessage error={formState.errors?.birthplace} />
            </FormControl>
          </Stack>
          <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
            <FormControl isInvalid={!!formState.errors?.birthCityPostalCode}>
              <FormLabel htmlFor="birthCityPostalCode">{i18nOwners('ZipCodeField')}</FormLabel>
              <Input id="birthCityPostalCode" type="string" {...register('birthCityPostalCode')} />
              <ErrorMessage error={formState.errors?.birthCityPostalCode} />
            </FormControl>
            <Controller
              control={control}
              name={'birthCountry'}
              render={({ field }): JSX.Element => (
                <FormControl isInvalid={!!formState.errors.birthCountry}>
                  <FormLabel htmlFor="country">{i18nOwners('CountryField')}</FormLabel>
                  <Select
                    id="birthCountry"
                    menuPlacement="auto"
                    options={alpha3CountryOptions}
                    {...field}
                  />
                  <ErrorMessage error={formState.errors?.birthCountry?.value} />
                </FormControl>
              )}
            />
          </Stack>
          <Stack bg="gray.50" borderRadius="md" p="3" spacing="2">
            <Text fontSize="lg" fontWeight="bold">
              {i18nOwners('Address.Title')}
            </Text>
            <Stack spacing="4">
              <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                <FormControl isInvalid={!!formState.errors?.address?.line}>
                  <FormLabel htmlFor="line">{i18nOwners('Address.LineField')}</FormLabel>
                  <Input id="line" type="string" {...register('address.line')} />
                  <ErrorMessage error={formState.errors?.address?.line} />
                </FormControl>
                <FormControl isInvalid={!!formState.errors?.address?.city}>
                  <FormLabel htmlFor="city">{i18nOwners('Address.CityField')}</FormLabel>
                  <Input id="city" type="string" {...register('address.city')} />
                  <ErrorMessage error={formState.errors?.address?.city} />
                </FormControl>
              </Stack>
              <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                <FormControl isInvalid={!!formState.errors?.address?.postalCode}>
                  <FormLabel htmlFor="postalCode">{i18nOwners('Address.ZipCodeField')}</FormLabel>
                  <Input id="postalCode" type="string" {...register('address.postalCode')} />
                  <ErrorMessage error={formState.errors?.address?.postalCode} />
                </FormControl>
                <Controller
                  control={control}
                  name="address.country"
                  render={({ field }): JSX.Element => (
                    <FormControl isInvalid={!!formState.errors?.address?.country}>
                      <FormLabel htmlFor="country">{i18nOwners('Address.CountryField')}</FormLabel>
                      <Select
                        id="country"
                        menuPlacement="auto"
                        options={alpha3CountryOptions}
                        {...field}
                      />
                      <ErrorMessage error={formState.errors?.address?.country?.value} />
                    </FormControl>
                  )}
                />
              </Stack>
            </Stack>
          </Stack>
          <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
            <Controller
              control={control}
              name={'role'}
              render={({ field }): JSX.Element => (
                <FormControl isInvalid={!!formState.errors?.role}>
                  <FormLabel htmlFor="type">{i18nOwners('ShareholderStatusField')} </FormLabel>
                  <RadioGroup id="role" {...field}>
                    <Stack>
                      {Object.keys(UboRole).map((role) => (
                        <Radio key={role} value={role}>
                          {i18nShareholderStatus(
                            role as keyof IntlMessages['ShareholderStatusValues'],
                          )}
                        </Radio>
                      ))}
                    </Stack>
                  </RadioGroup>
                  <ErrorMessage error={formState.errors?.role} />
                </FormControl>
              )}
            />
          </Stack>
          {!isRepresentativeStatus && (
            <Stack spacing="4">
              <FormControl isInvalid={!!formState.errors?.sharePercentage}>
                <FormLabel htmlFor="percentage">{i18nOwners('PercentageField')}</FormLabel>
                <InputGroup>
                  <Input
                    id="sharePercentage"
                    max={maxSharePercentage}
                    step="0.01"
                    type="number"
                    {...register('sharePercentage')}
                  />
                  <InputRightAddon>%</InputRightAddon>
                </InputGroup>
                <ErrorMessage error={formState.errors?.sharePercentage} />
              </FormControl>
              <FormControl>
                <Checkbox {...register('isDirect')}>
                  <Text>{i18nOwners('IsDirectField')}</Text>
                </Checkbox>
              </FormControl>
            </Stack>
          )}
        </Stack>
        <HStack justifyContent="space-between">
          <Button
            type="button"
            variant="secondary"
            w="full"
            onClick={(): void => handleFormCancel()}
          >
            {i18nGeneral('Delete')}
          </Button>
          <Button type="submit" w="full">
            {i18nGeneral('Submit')}
          </Button>
        </HStack>
      </Stack>
    </form>
  );
}

export type CompanyOwnerFormProps = Props;
